/**
 * Client-side file validation utilities
 * Temporary workaround for Vite CommonJS import issues
 */

// Inline constants to avoid CommonJS import issues
const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
const SUPPORTED_DOCUMENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
];

const ERROR_CODES = {
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_FORMAT: 'VALIDATION_INVALID_FORMAT',
  VALIDATION_FILE_TOO_LARGE: 'VALIDATION_FILE_TOO_LARGE',
  VALIDATION_UNSUPPORTED_FILE_TYPE: 'VALIDATION_UNSUPPORTED_FILE_TYPE',
} as const;

export interface FileValidationError {
  field: string;
  message: string;
  code: string;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: FileValidationError[];
}

export function validateFileForUpload(
  fileName: string,
  fileType: string,
  fileSize: number
): FileValidationResult {
  const errors: FileValidationError[] = [];

  if (!fileName || fileName.trim().length === 0) {
    errors.push({
      field: 'fileName',
      message: 'File name is required',
      code: ERROR_CODES.VALIDATION_REQUIRED_FIELD,
    });
  }

  if (!fileType || fileType.trim().length === 0) {
    errors.push({
      field: 'fileType',
      message: 'File type is required',
      code: ERROR_CODES.VALIDATION_REQUIRED_FIELD,
    });
  } else {
    const normalizedType = fileType.toLowerCase();
    if (!SUPPORTED_DOCUMENT_TYPES.includes(normalizedType)) {
      errors.push({
        field: 'fileType',
        message: `Unsupported file type. Supported types: PDF, JPEG, PNG, DOCX`,
        code: ERROR_CODES.VALIDATION_UNSUPPORTED_FILE_TYPE,
      });
    }
  }

  if (fileSize <= 0) {
    errors.push({
      field: 'fileSize',
      message: 'File size must be greater than 0',
      code: ERROR_CODES.VALIDATION_INVALID_FORMAT,
    });
  } else if (fileSize > MAX_DOCUMENT_SIZE_BYTES) {
    const maxSizeMB = MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024);
    errors.push({
      field: 'fileSize',
      message: `File size exceeds maximum limit of ${maxSizeMB}MB`,
      code: ERROR_CODES.VALIDATION_FILE_TOO_LARGE,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function getFileValidationErrorMessage(result: FileValidationResult): string | null {
  if (result.isValid) {
    return null;
  }

  if (result.errors.length === 1) {
    return result.errors[0].message;
  }

  return `Multiple validation errors:\n${result.errors.map(e => `• ${e.message}`).join('\n')}`;
}

export function getMaxFileSizeDisplay(): string {
  const maxSizeMB = MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024);
  return `${maxSizeMB}MB`;
}

export function getSupportedFileTypesDisplay(): string {
  return 'PDF, JPEG, PNG, DOCX';
}

export function getFileInputAcceptAttribute(): string {
  return '.pdf,.jpg,.jpeg,.png,.doc,.docx';
}
